import React, { useState, useEffect } from 'react';
import { createPageUrl } from "@/utils";
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  ExternalLink,
  HelpCircle
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { runEndpoint } from '@/services/flow';
import { useNavigate } from 'react-router-dom';

function getScoreColor(score, max) {
  const pct = (score / max) * 100;
  if (pct >= 75) return '#4DB300';
  if (pct >= 50) return 'rgba(233,171,48,0.9)';
  return 'rgba(222,111,107,0.9)';
}

function ScoreBar({ value, max }) {
  const pct = Math.min((value / max) * 100, 100);
  const color = getScoreColor(value, max);
  return (
    <div className="h-1.5 rounded-full mt-2" style={{ background: 'var(--card-border)' }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function ACTSummaryCard({ dataSourceId, datePath, selectedFile }) {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dataSourceId && datePath) {
      loadStatistics();
    }
  }, [dataSourceId, datePath]);

  const loadStatistics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await runEndpoint('cortex.get_act_statistics', {
        dataSourceId: parseInt(dataSourceId),
        datePath
      });
      if (response?.error) {
        setError(response.error);
        setStatistics(null);
      } else {
        setStatistics(response);
      }
    } catch (err) {
      console.error('Error loading ACT statistics:', err);
      setError('Failed to load ACT statistics');
      setStatistics(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewFullStatistics = () => {
    navigate(createPageUrl(`data-sources/${dataSourceId}/statistics?datePath=${encodeURIComponent(datePath)}`));
  };

  if (!datePath) return null;

  if (isLoading) {
    return (
      <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={18} color="#377AD6" className="shrink-0" />
          <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>ACT Quality Scores</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 rounded-[8px]" />)}
        </div>
      </div>
    );
  }

  if (error || !statistics?.summary) {
    return (
      <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={18} color="#377AD6" className="shrink-0" />
          <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>ACT Quality Scores</h2>
        </div>
        <div className="flex items-start gap-2 text-sm" style={{ color: 'var(--page-muted)' }}>
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">{error || 'No ACT statistics available for this file. Run processing first.'}</span>
        </div>
      </div>
    );
  }

  const { summary } = statistics;

  return (
    <div className="bg-card-bg rounded-[8px] p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <BarChart3 size={18} color="#377AD6" className="shrink-0" />
          <h2 className="text-base font-medium min-w-0 break-words" style={{ color: 'var(--page-text)' }}>
            ACT Quality Scores
            {selectedFile && (
              <span className="font-normal text-sm ml-2" style={{ color: 'var(--page-muted)' }}>
                for {selectedFile.label}
              </span>
            )}
          </h2>
        </div>
        <button
          onClick={handleViewFullStatistics}
          className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
        >
          <ExternalLink size={16} strokeWidth={2.5} />
          View Full Statistics
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
        {/* Accuracy */}
        <div className="p-3 md:p-4 rounded-[8px] min-w-0" style={{ background: 'var(--table-stripe)' }}>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-2">
            <CheckCircle2 size={16} color="#377AD6" className="shrink-0" />
            <span className="text-sm min-w-0" style={{ color: 'var(--page-muted)' }}>Accuracy</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle size={14} style={{ color: 'var(--page-muted)', cursor: 'help' }} className="shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-semibold mb-1">Average Accuracy Score</p>
                  <p className="text-xs">Average of all field accuracy scores across all records. Calculated as: sum of all accuracy scores ÷ total field count. Shows what percentage of field values are valid (not blank, null, or "Unknown").</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-2xl font-bold" style={{ color: getScoreColor(summary.avgAccuracyScore, 1) }}>
            {(summary.avgAccuracyScore * 100).toFixed(1)}%
          </div>
          <ScoreBar value={summary.avgAccuracyScore} max={1} />
        </div>

        {/* Completeness */}
        <div className="p-3 md:p-4 rounded-[8px] min-w-0" style={{ background: 'var(--table-stripe)' }}>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-2">
            <TrendingUp size={16} color="rgba(109,40,217,0.85)" className="shrink-0" />
            <span className="text-sm min-w-0" style={{ color: 'var(--page-muted)' }}>Completeness</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle size={14} style={{ color: 'var(--page-muted)', cursor: 'help' }} className="shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-semibold mb-1">Average Completeness Score</p>
                  <p className="text-xs">Average of all field completeness scores across all records. Calculated as: sum of all completeness scores ÷ total field count. Each field scores 0-3 based on presence and importance weight.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-2xl font-bold" style={{ color: getScoreColor(summary.avgCompletenessScore, 3) }}>
            {summary.avgCompletenessScore.toFixed(2)}
          </div>
          <div className="text-xs" style={{ color: 'var(--page-muted)' }}>out of 3</div>
          <ScoreBar value={summary.avgCompletenessScore} max={3} />
        </div>

        {/* Timeliness */}
        <div className="p-3 md:p-4 rounded-[8px] min-w-0" style={{ background: 'var(--table-stripe)' }}>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-2">
            <Clock size={16} color="rgba(234,88,12,0.85)" className="shrink-0" />
            <span className="text-sm min-w-0" style={{ color: 'var(--page-muted)' }}>Timeliness</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle size={14} style={{ color: 'var(--page-muted)', cursor: 'help' }} className="shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-semibold mb-1">Average Timeliness Score</p>
                  <p className="text-xs">Average of all field timeliness scores across all records. Calculated as: sum of all timeliness scores ÷ total field count. Each field scores 0-4 based on data age and time-sensitivity weight.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-2xl font-bold" style={{ color: getScoreColor(summary.avgTimelinessScore, 4) }}>
            {summary.avgTimelinessScore.toFixed(2)}
          </div>
          <div className="text-xs" style={{ color: 'var(--page-muted)' }}>out of 4</div>
          <ScoreBar value={summary.avgTimelinessScore} max={4} />
        </div>

        {/* Total ACT */}
        <div className="p-3 md:p-4 rounded-[8px] min-w-0" style={{ background: 'var(--table-stripe)' }}>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-2">
            <BarChart3 size={16} color="#4DB300" className="shrink-0" />
            <span className="text-sm min-w-0" style={{ color: 'var(--page-muted)' }}>Total ACT</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle size={14} style={{ color: 'var(--page-muted)', cursor: 'help' }} className="shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-semibold mb-1">Average Total ACT Score</p>
                  <p className="text-xs">Average of all field total ACT scores across all records. Calculated as: sum of all (accuracy + completeness + timeliness) scores ÷ total field count. Range 0-8.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-2xl font-bold" style={{ color: getScoreColor(summary.avgTotalActScore, 8) }}>
            {summary.avgTotalActScore.toFixed(2)}
          </div>
          <div className="text-xs" style={{ color: 'var(--page-muted)' }}>out of 8</div>
          <ScoreBar value={summary.avgTotalActScore} max={8} />
        </div>
      </div>

      {/* Summary stats */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-4" style={{ borderTop: '1px solid var(--card-border)' }}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" style={{ color: 'var(--page-text)' }}>
          <span className="flex items-center gap-1">
            <strong>{summary.totalRecords?.toLocaleString()}</strong>
            <span style={{ color: 'var(--page-muted)' }}>records</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle size={12} style={{ color: 'var(--page-muted)', cursor: 'help' }} />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">Total number of unique records (e.g., ships) in this dataset.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
          <span className="flex items-center gap-1">
            <strong>{summary.totalFields?.toLocaleString()}</strong>
            <span style={{ color: 'var(--page-muted)' }}>field values</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle size={12} style={{ color: 'var(--page-muted)', cursor: 'help' }} />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs">Total number of individual field values across all records. Average scores are calculated per field.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
        {summary.recordsBelowThreshold > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap cursor-help"
                  style={{ background: 'rgba(222,111,107,0.20)', color: 'rgba(222,111,107,0.90)' }}
                >
                  <AlertTriangle size={10} />
                  {summary.recordsBelowThreshold} records below threshold
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">Records with an average ACT score per field below 4 (50% of maximum). These records may need attention.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
